/**
 * @file: 医典 request
 */
import { checkResponse, checkResponseCode } from '@/utils/helpers';
import {
  IOrderInfo,
  IAssistantOrderListResult,
  ORDER_STATUS,
  PRODUCT_TYPE
} from '@/schemas/order';
import { RESPONSE_SUCCESS_CODE } from '@/configs/constants';
import Toast from '@/components/Toast';
import { PRESCRIPTION_ORDER_ID_TIME_LIST } from '@/schemas/common';
import { request, TAxiosRequestConfig } from './request';
import { ApiConfigs, Response, ResponseV2 } from '../schemas/service';
import {
  IServiceManageItem,
  IDcotorChatSessionItem,
  DoctorWorkTimeInfo,
  DoctorWorkTimeInfoForUser
} from '../schemas/workstation';

const api: ApiConfigs = {
  queryDoctorReplyInfo: {
    url: '/health/api/v1/doctorEnd/workbench/queryDoctorServiceInfo',
    method: 'GET'
  },
  queryOrderList: {
    url: '/health/api/v1/doctorEnd/workbench/queryOrderByDoctorId',
    method: 'GET'
  },
  queryWorkstationOrderList: {
    url: '/health/api/v1/doctorEnd/workbench/queryConsultingOrders',
    method: 'GET'
  },
  queryDoctorPushSwitch: {
    url: '/health/api/v1/doctorEnd/workbench/queryDoctorPushSwitch',
    method: 'GET'
  },
  updateDoctorPushSwitch: {
    url: '/health/api/v1/doctorEnd/workbench/updateDoctorPushSwitch',
    method: 'POST'
  },
  // 获取服务管理数据
  getServiceManageData: {
    url: '/health/api/v1/doctor/queryDoctorServiceManager',
    method: 'GET'
  },
  queryPrescriberServiceInfo: {
    url: '/health/api/v1/doctorEnd/workbench/queryPrescriberServiceInfo',
    method: 'GET'
  },
  queryPrescriptions: {
    url: '/health/api/v1/doctorEnd/workbench/queryPrescriptions',
    // url:
    //   'https://kapi.corp.kuaishou.com/mock/5058/health/api/v1/doctorEnd/workbench/queryPrescriptions',
    method: 'GET'
  },
  // 获取会话列表
  queryDoctorChatSessions: {
    url: '/health/api/v1/doctorEnd/consult/queryDoctorChatSessions',
    // url:
    //   'https://kapi.corp.kuaishou.com/mock/5058/health/api/v1/doctorEnd/consult/queryDoctorChatSessions',
    method: 'GET'
  },
  // 查询新功能弹窗
  getNewFeaturePopup: {
    url: '/health/api/v1/activity/getNewFeaturePopup',
    method: 'GET'
  },
  // 上报新功能弹窗
  reportNewFeaturePopup: {
    url: '/health/api/v1/activity/reportNewFeaturePopup',
    method: 'POST'
  },

  // 获取工作台-私信总览
  getDoctorMainMsgTotalCount: {
    url: '/health/api/v1/doctorEnd/msg/count',
    method: 'GET'
  },
  // 获取工作台-分页获取私信信息
  queryDoctorMainMsgList: {
    url: '/health/api/v1/doctorEnd/msg/list',
    method: 'GET'
  },

  // 获取工作台-医生功能分类接口
  getDoctorServiceTabs: {
    url: '/health/api/v1/doctorEnd/workbench/entrance',
    method: 'GET'
  },
  getDoctorWorkTimeInfo: {
    url: '/health/api/v1/doctorEnd/work/time-info',
    method: 'GET'
  },
  getDoctorWorkTimeInfoForUser: {
    url: '/health/api/v1/work/time-info',
    method: 'GET'
  },
  deleteDoctorWorkTimeInfo: {
    url: '/health/api/v1/doctorEnd/work/time-info/delete',
    method: 'POST'
  }
};

export interface IWorkstationInfo {
  doctorAvatar: string;
  doctorName: string;
  doctorRankName: string;
  generalOrderCount: number;
  orderCount: number;
  replyRate: number;
  replyTime: number;
  toBeRepliedCount: number;
  voiceCallOrderCount: number;
  hasNewMsg: boolean;
  jobType: number;
}

export interface IAssistantInfo {
  doctorAvatar: string; // 医生头像
  doctorName: string; // 医生姓名
  doctorRankName: string; // 医生职级名称
  generalOrderCount: number; // 待开方量
  cooperatorCount: number; // 协作医生数量
  prescriptionCount: number; // 开方量-咨询订单进行中的
  allPrescriptionCount: number; // 开方量-累计
  replyTime: number; // 平均响应时长
  hasNewMsg: boolean; // 是否有新消息
  jobType: number; // 1全职|2兼职|3开方医生
}

interface IOrderListQuery {
  pageNum: number;
  pageSize: number;
  productType?: PRODUCT_TYPE;
  status?: ORDER_STATUS;
}

export interface IDoctorServiceTab {
  key: number;
  value: string;
}

export interface IDoctorEntrance {
  idFaceResult: idFaceResult;
  serviceTabs: IDoctorServiceTabInfo[]; // 医生开展的服务类型的排序数组
}
export interface idFaceResult {
  resultCode: number;
  url: string;
  needCheck: boolean; // 是否进行实名校验及人脸认证
  checkType: number; // 0-非强制校验，1-强制校验
}

export interface IDoctorServiceTabInfo {
  index: number;
  title: string;
  code: string;
  whiteList: [];
}

export interface IDoctorMainMsgListQuery {
  offset: number;
}

export interface IWorkstationOrderListResult {
  lastMark: boolean;
  list: IOrderInfo[];
  pageNum: number;
  pageSize: number;
  total: number;
}

export interface IDoctorMainMsgInfo {
  contentType: number; // 消息的contentType值
  unReadText: string; // 未读文案
  showEnterButton: boolean; // 是否进入私信按钮
  msgBody: string; // 消息内容
  nickName: string; // 快手账号名字
  userId: number; // 患者的userid
  timeDesc: string; // 消息发送时间描述
  groupId: string; // 群聊ID
  timestampMs: string;
}

export interface IDocotorMainMsgListResult {
  hasMore: boolean; // 是否还有更多
  nextOffset: number;
  unread: IDoctorMainMsgInfo[];
  serving: IDoctorMainMsgInfo[];
  blackList: IDoctorMainMsgInfo[];
}

export enum WorkstationOrderType {
  MESSAGE = 1,
  VOICE_CALL = 2,
  NEED_REPLY = 3
}

export const WorkstationOrderTypeName = {
  [WorkstationOrderType.MESSAGE]: '图文待接单',
  [WorkstationOrderType.VOICE_CALL]: '电话待接单',
  [WorkstationOrderType.NEED_REPLY]: '待回复'
};

export enum DoctorServiceType {
  CLINIC = 1,
  MAIN_MESSAGES = 2,
  LIVE = 3,
  TIME_INFO = 4
}

export const DoctorServiceCode = {
  CLINIC: 'CLINIC',
  MAIN_MESSAGES: 'MAIN_MESSAGES',
  LIVE: 'LIVE',
  TIME_INFO: 'TIME_INFO'
};

export const DoctorServiceTypeName = {
  [DoctorServiceType.CLINIC]: '我的诊室',
  [DoctorServiceType.MAIN_MESSAGES]: '我的私信'
  // [DoctorServiceType.LIVE]: '我的直播'
};

export enum DoctorMainMsgType {
  NEED_ANSWER = 1,
  CONNECT = 2,
  BLACK_LIST = 3
}

export type TMainMsgLists = {
  [DoctorMainMsgType.NEED_ANSWER]: IDoctorMainMsgInfo[];
  [DoctorMainMsgType.CONNECT]: IDoctorMainMsgInfo[];
  [DoctorMainMsgType.BLACK_LIST]: IDoctorMainMsgInfo[];
};

export const DoctorMainMsgTypeName = {
  [DoctorMainMsgType.NEED_ANSWER]: '待回复',
  [DoctorMainMsgType.CONNECT]: '沟通中',
  [DoctorMainMsgType.BLACK_LIST]: '已拉黑'
};
export enum AssistantWorkstationOrderType {
  TO_PRESCRIPT = 1, // 待开方
  NEED_REPLY = 2 // 待回复
}

export const AssistantWorkstationOrderTypeName = {
  [AssistantWorkstationOrderType.TO_PRESCRIPT]: '待开方',
  [AssistantWorkstationOrderType.NEED_REPLY]: '待回复'
};

const workstation = {
  async getDoctorInfo(options?: TAxiosRequestConfig) {
    try {
      const response: Response<IWorkstationInfo> = await request(
        api.queryDoctorReplyInfo,
        undefined,
        undefined,
        options
      );
      const { code, result } = response;
      if (checkResponse(code, result)) {
        return result;
      }
      return {} as IWorkstationInfo;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getOrderList(queryObj: IOrderListQuery) {
    try {
      const response: Response<IWorkstationOrderListResult> = await request(
        api.queryOrderList,
        { ...queryObj }
      );
      const { code, result } = response;
      if (checkResponse(code, result)) {
        return result;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getDocotorMainMsgList(
    queryObj: IDoctorMainMsgListQuery,
    options?: TAxiosRequestConfig
  ) {
    try {
      const response: Response<IDocotorMainMsgListResult> = await request(
        api.queryDoctorMainMsgList,
        { ...queryObj },
        undefined,
        options
      );

      const { code, result } = response;
      if (checkResponse(code, result)) {
        const { unread, serving, blackList, hasMore, nextOffset } = result;
        return {
          hasMore,
          nextOffset,
          messagesLists: {
            [DoctorMainMsgType.NEED_ANSWER]: unread,
            [DoctorMainMsgType.CONNECT]: serving,
            [DoctorMainMsgType.BLACK_LIST]: blackList
          },
          msgTotalCount: {
            [DoctorMainMsgType.NEED_ANSWER]: unread.length,
            [DoctorMainMsgType.CONNECT]: serving.length,
            [DoctorMainMsgType.BLACK_LIST]: blackList.length
          }
        };
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getWorkstationOrderList(
    { pageNum = 1, pageSize = 10, tabType = WorkstationOrderType.MESSAGE },
    options?: TAxiosRequestConfig
  ) {
    try {
      const response: Response<IWorkstationOrderListResult> = await request(
        api.queryWorkstationOrderList,
        {
          pageNum,
          pageSize,
          tabType
        },
        undefined,
        options
      );
      const { code, result } = response;
      if (checkResponse(code, result)) {
        return result;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // 获取到医生的服务tab数据
  async getDoctorServiceTab() {
    try {
      const response: Response<IDoctorEntrance> = await request(
        api.getDoctorServiceTabs
      );
      const { code, result } = response;
      if (checkResponse(code, result)) {
        return result.serviceTabs;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getDoctorPushSwitch() {
    try {
      const response: Response<boolean> = await request(
        api.queryDoctorPushSwitch
      );
      const { code, message, result } = response;
      if (checkResponseCode(code)) {
        return result;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async updateDoctorPushSwitch({ switchType }) {
    try {
      const response: any = await request(
        api.updateDoctorPushSwitch,
        { switchType },
        null,
        {
          needResponse: true
        }
      );
      const { code, message } = response;
      if (code === RESPONSE_SUCCESS_CODE) {
        return true;
      }
      Toast.show(message);
      return false;
    } catch (err) {
      return Promise.reject(err);
    }
  },

  async getServiceManageData() {
    try {
      const response: Response = await request(
        api.getServiceManageData,
        undefined,
        undefined,
        { hideGlobalLoading: false }
      );
      const { result } = response;
      return result as IServiceManageItem[];
    } catch (err) {
      return Promise.reject(err);
    }
  },

  async queryPrescriberServiceInfo(options?: TAxiosRequestConfig) {
    try {
      const response: Response<IAssistantInfo> = await request(
        api.queryPrescriberServiceInfo,
        undefined,
        undefined,
        options
      );
      const { code, result } = response;
      if (checkResponse(code, result)) {
        return result;
      }
      return {} as IAssistantInfo;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async queryPrescriptions(
    {
      pageNum = 1,
      pageSize = 10,
      tabType = AssistantWorkstationOrderType.TO_PRESCRIPT
    },
    options?: TAxiosRequestConfig
  ) {
    try {
      const response: Response<IAssistantOrderListResult> = await request(
        api.queryPrescriptions,
        {
          pageNum,
          pageSize,
          tabType
        },
        undefined,
        options
      );
      const { code, result } = response;
      if (checkResponse(code, result)) {
        return result;
      }
      return Promise.reject(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  // 获取医生工作台消息列表
  async queryDoctorChatSessions({
    hideLoading,
    changeListOrder,
    changeContent
  }) {
    try {
      const response: Response<IDcotorChatSessionItem[]> = await request(
        api.queryDoctorChatSessions,
        undefined,
        undefined,
        {
          hideGlobalLoading: hideLoading
        }
      );
      const { result } = response;
      if (changeListOrder) {
        /* eslint-disable no-unused-expressions , no-param-reassign */
        // 获取localStorage预开方编辑时间重新排序消息列表
        const editHistory = JSON.parse(
          localStorage.getItem(PRESCRIPTION_ORDER_ID_TIME_LIST) || '[]'
        );
        if (editHistory?.length && result?.length) {
          let changeFlag = false;
          // 更新消息列表中的时间和高亮显示信息
          editHistory?.forEach((item) => {
            result?.find((sessionItem) => {
              if (sessionItem?.orderId === Number(item?.orderId)) {
                sessionItem.highLightText = changeContent;
                if (sessionItem?.activeTime < item?.time) {
                  sessionItem.activeTime = item?.time;
                  changeFlag = true;
                }
                return true;
              }
              return false;
            });
          });
          if (changeFlag) {
            result?.sort((pre, after) => {
              return after?.activeTime - pre?.activeTime;
            });
          }
        }
      }

      return result
        ? (result as IDcotorChatSessionItem[])
        : Promise.reject(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getNewFeaturePopup({ type }) {
    try {
      const response: Response<boolean> = await request(
        api.getNewFeaturePopup,
        {
          type
        }
      );
      const { result } = response;
      return typeof result === 'boolean' ? result : Promise.reject(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async reportNewFeaturePopup({ type }) {
    try {
      const response: Response<number> = await request(
        api.reportNewFeaturePopup,
        { type }
      );
      const { result } = response;
      return typeof result === 'number' ? result : Promise.reject(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getDoctorWorkTimeInfo() {
    try {
      const response: Response<DoctorWorkTimeInfo> = await request(
        api.getDoctorWorkTimeInfo
      );
      const { result } = response;
      return {
        workDateList: result.workInfo.map((item) => item.workDate),
        workTimeInfo: result.workInfo.map((item) => item.workTimeInfo)
      };
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getDoctorWorkTimeInfoForUser(doctorId: string) {
    try {
      const response: Response<DoctorWorkTimeInfoForUser> = await request(
        api.getDoctorWorkTimeInfoForUser,
        { doctorId }
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async deleteDoctorWorkTimeInfo(id: number) {
    try {
      const response: Response<DoctorWorkTimeInfoForUser> = await request(
        api.deleteDoctorWorkTimeInfo,
        { id }
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  }
};

export default workstation;
